import BrandComparisonChart from "@/components/charts/BrandComparisonChart";
import MentionRateComparisonChart from "@/components/charts/MentionRateComparisonChart";
import PlatformVisibilityChart from "@/components/charts/PlatformVisibilityChart";
import UserIntentTable from "@/components/charts/UserIntentTable";
import MobileTooltip from "@/components/common/MobileTooltip";
import QABrowserModal from "@/components/modals/QABrowserModal";
import { useBetaStatus } from "@/hooks/useBetaStatus";
import { useQuestionList } from "@/hooks/useQuestionList";
import type { BrandAiTrendPoint } from "@/services/api/brandAiMetricsTrend";
import type { BrandMetricsRates } from "@/services/api/brandMetricsRates";
import type { BrandRatesItem } from "@/services/api/BrandRates";
import { reputationDetailApi } from "@/services/api/reputationDetail";
import { formatPercent } from "@/utils/format";
import {
  FrownOutlined,
  LineChartOutlined,
  SmileOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import { Grid } from "antd";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

// 样式常量
// 样式常量 removed - migrated to LESS
import "./InsightAnalysisSection.less";

interface InsightAnalysisSectionProps {
  brandName: string;
  brandId?: string;
  categoryId?: string;
  selectedCompetitors: string[];
  setSelectedCompetitors: (next: string[]) => void;
  selectedPlatforms: string[];
  brandMetrics?: BrandMetricsRates | null;
  brandMetricsLoading?: boolean;
  brandAiMetricsTrend?: BrandAiTrendPoint[];
  brandComparison?: any; // 新增：品牌对比数据
  brandRatesList?: BrandRatesItem[];
  days?: number; // 新增：时间范围天数
  onDownload?: () => void; // 新增：下载回调函数
}

// UNIFIED_TOOLTIP 已移至组件内部，使用国际化动态生成
// METRICS_CONFIG moved inside component

const InsightAnalysisSection: React.FC<InsightAnalysisSectionProps> = ({
  brandName,
  brandId,
  categoryId,
  selectedCompetitors,
  setSelectedCompetitors,
  selectedPlatforms,
  brandMetrics,
  brandMetricsLoading,
  brandAiMetricsTrend,
  brandComparison, // 新增：品牌对比数据
  brandRatesList,
  days, // 新增：时间范围天数
  onDownload,
}) => {
  const { t } = useTranslation();

  // 统一指标 tooltip - 使用国际化
  const UNIFIED_TOOLTIP = `${t("api.metric_mention_rate_tooltip")}

${t("api.metric_first_mention_rate_tooltip")}

${t("api.metric_positive_sentiment_tooltip")}

${t("api.metric_negative_sentiment_tooltip")}

${t("api.metric_tooltip_note")}`;

  const METRICS_CONFIG = {
    mentionRate: {
      key: "mentionRate",
      title: t("search_detail.insight_mention_analysis"),
      tooltip: UNIFIED_TOOLTIP,
      icon: <LineChartOutlined style={{ fontSize: 20 }} />,
      color: "#8b5cf6", // Purple-ish
      bg: "#ede9fe",
    },
    firstMentionRate: {
      key: "firstMentionRate",
      title: t("search_detail.insight_first_mention"),
      tooltip: UNIFIED_TOOLTIP,
      icon: <TrophyOutlined style={{ fontSize: 20 }} />,
      color: "#21C4B8", // Teal
      bg: "#ccfbf6",
    },
    /*   firstRecommendationRate: {
        key: 'firstRecommendationRate',
        title: t('search_detail.insight_first_recommend'),
        tooltip: UNIFIED_TOOLTIP
      }, */
    positiveMentionRate: {
      key: "positiveMentionRate",
      title: t("search_detail.insight_metric_positive"),
      tooltip: UNIFIED_TOOLTIP,
      icon: <SmileOutlined style={{ fontSize: 20 }} />,
      color: "#3b82f6", // Blue
      bg: "#dbeafe",
    },
    negativeMentionRate: {
      key: "negativeMentionRate",
      title: t("search_detail.insight_metric_negative"),
      tooltip: UNIFIED_TOOLTIP,
      icon: <FrownOutlined style={{ fontSize: 20 }} />,
      color: "#ef4444", // Red
      bg: "#fee2e2",
    },
  };
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [selectedMetric, setSelectedMetric] = useState<string>("mentionRate");
  const [qaModalVisible, setQaModalVisible] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<string>("");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string>("");
  const [selectedModelName, setSelectedModelName] = useState<string>("");
  const [selectedLinksNum, setSelectedLinksNum] = useState<number>(0);
  const [selectedTaskId, setSelectedTaskId] = useState<string>("");
  // tooltipModalVisible 已移至 MobileTooltip 组件内部管理

  // 动态问题参数
  const questionParams =
    brandId && categoryId
      ? {
          brandId,
          categoryId,
          days: days || 7,
          metricType: selectedMetric, // 跟随当前指标
          modelId: selectedPlatforms?.length ? selectedPlatforms[0] : "all",
        }
      : undefined;

  // 获取问题列表用于模态框导航
  const questionListData = useQuestionList(
    questionParams || {
      brandId: brandId || "",
      categoryId: categoryId || "",
      days: days || 7,
      metricType: selectedMetric,
      modelId: selectedPlatforms?.length ? selectedPlatforms[0] : "all",
    }
  );
  const questionList = questionListData.questions.map((q) => q.question);

  // Beta 限制：受限时（isBeta=true）仅允许查看前三条
  const { isBeta } = useBetaStatus();
  const visibleQuestionCount = Math.min(
    questionList.length,
    isBeta ? 3 : questionList.length
  );

  // 统一规则：仅在头部t('navbar.menu_join')触发；此处通过 Header 的导航 hook 触发
  // const { selectContact } = useHeaderNav() // 移除直接使用 hook
  const [isWide, setIsWide] = React.useState<boolean>(
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true
  );

  React.useEffect(() => {
    const onResize = () => setIsWide(window.innerWidth >= 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // 获取当前选中指标的配置
  const currentMetric =
    METRICS_CONFIG[selectedMetric as keyof typeof METRICS_CONFIG] ||
    METRICS_CONFIG.mentionRate;

  // 处理问题点击事件
  const handleQuestionClick = async (question: string, index: number) => {
    console.log("🎯 点击问题:", question, "索引:", index);
    setSelectedQuestion(question);
    setCurrentQuestionIndex(index);
    setQaModalVisible(true);
    try {
      const item: any = questionListData.questions[index];
      const taskId = item?.taskId;
      if (!taskId) {
        setSelectedAnswer("");
        setSelectedTaskId("");
        return;
      }
      setSelectedTaskId(taskId);
      const data = await reputationDetailApi.getReputationKeywordDetail({
        taskId,
        brandId: brandId || "brand-mock",
        categoryId: categoryId || "category-mock",
        days: days || 7,
        metricType: selectedMetric as any,
        modelId: selectedPlatforms?.length ? selectedPlatforms[0] : "all",
      });
      setSelectedAnswer(data?.answer || "");
      setSelectedModelName(data?.modelName || "");
      setSelectedLinksNum(data?.linksNum || 0);
    } catch (e) {
      console.error(t("search_detail.insight_error_load"), e);
      setSelectedAnswer("");
      setSelectedModelName("");
      setSelectedTaskId("");
    }
  };

  // 处理上一个问题
  const handlePrevQuestion = async () => {
    if (currentQuestionIndex > 0) {
      const newIndex = currentQuestionIndex - 1;
      setCurrentQuestionIndex(newIndex);
      setSelectedQuestion(questionList[newIndex]);
      try {
        const item: any = questionListData.questions[newIndex];
        const taskId = item?.taskId;
        if (!taskId) {
          setSelectedAnswer("");
          setSelectedTaskId("");
          return;
        }
        // 更新taskId状态，确保CitelistModal能获取正确的taskId
        setSelectedTaskId(taskId);
        const data = await reputationDetailApi.getReputationKeywordDetail({
          taskId,
          brandId: brandId || "brand-mock",
          categoryId: categoryId || "category-mock",
          days: days || 7,
          metricType: selectedMetric as any,
          modelId: selectedPlatforms?.length ? selectedPlatforms[0] : "all",
        });
        setSelectedAnswer(data?.answer || "");
        setSelectedModelName(data?.modelName || "");
        setSelectedLinksNum(data?.linksNum || 0);
      } catch {
        setSelectedAnswer("");
        setSelectedTaskId("");
      }
    }
  };

  // 处理下一个问题
  const handleNextQuestion = async () => {
    const maxIndex = visibleQuestionCount - 1;
    if (currentQuestionIndex < maxIndex) {
      const newIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(newIndex);
      setSelectedQuestion(questionList[newIndex]);
      try {
        const item: any = questionListData.questions[newIndex];
        const taskId = item?.taskId;
        if (!taskId) {
          setSelectedAnswer("");
          setSelectedTaskId("");
          return;
        }
        // 更新taskId状态，确保CitelistModal能获取正确的taskId
        setSelectedTaskId(taskId);
        const data = await reputationDetailApi.getReputationKeywordDetail({
          taskId,
          brandId: brandId || "brand-mock",
          categoryId: categoryId || "category-mock",
          days: days || 7,
          metricType: selectedMetric as any,
          modelId: selectedPlatforms?.length ? selectedPlatforms[0] : "all",
        });
        setSelectedAnswer(data?.answer || "");
        setSelectedModelName(data?.modelName || "");
        setSelectedLinksNum(data?.linksNum || 0);
      } catch {
        setSelectedAnswer("");
        setSelectedTaskId("");
      }
    }
  };

  return (
    <div
      ref={rootRef}
      className={`insight-analysis-section ${isMobile ? "mobile" : ""}`}
    >
      {/* 动态标题 + 竞品选择 */}
      <div className="section-header">
        <span className="title">
          {t("search_detail.insight_index_overview")}
        </span>
        <MobileTooltip
          content={currentMetric.tooltip}
          title={
            t("search_detail.insight_index_overview") +
            " - " +
            t("search_detail.insight_metric_desc")
          }
        />
      </div>

      {/* 现代化数据卡片 */}
      <div className="metrics-card-container">
        <div className="metrics-grid">
          {Object.entries(METRICS_CONFIG).map(([key, config]) => {
            const isSelected = selectedMetric === key;
            const value = brandMetrics?.[key as keyof typeof brandMetrics] || 0;

            return (
              <div
                key={key}
                onClick={() => setSelectedMetric(key)}
                className={`metric-card ${isSelected ? "active" : ""} ${isMobile ? "mobile" : ""}`}
              >
                <div className="card-header">
                  <div className="metric-title">
                    {config.title.replace("分析", "")}
                  </div>
                  <div
                    className="metric-icon"
                    style={{
                      color: config.color,
                      backgroundColor: config.bg,
                    }}
                  >
                    {config.icon}
                  </div>
                </div>
                <div className="card-body">
                  <div className="metric-value">
                    {brandMetricsLoading ? "--" : formatPercent(value, 1)}
                  </div>
                  {/* Trend Badge Placeholder - Data not available yet */}
                  {/* <div className="trend-badge positive">+2.4%</div> */}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 品牌对比分析图表 */}
      {/* 外部标题（使用图表内部原有文案规则） */}
      {/* 品牌对比分析图表 */}
      {/* 外部标题（使用图表内部原有文案规则） */}
      <div className="chart-section">
        <div className="section-title-wrapper">
          <span className="section-title">
            {t("home_components.chart_brand") +
              METRICS_CONFIG[selectedMetric as keyof typeof METRICS_CONFIG]
                ?.title}
          </span>
        </div>
        <div className="chart-container">
          <BrandComparisonChart
            brandId={brandId}
            brandName={brandName}
            categoryId={categoryId}
            days={days || 7}
            currentMetric={selectedMetric}
            onMetricChange={setSelectedMetric}
            showBrandSelector={false}
            showBrandButtons={true}
            selectedCompetitors={selectedCompetitors}
            onCompetitorChange={setSelectedCompetitors}
            metricInfo={currentMetric}
            brandComparison={brandComparison} // 新增：传递品牌对比数据
          />
        </div>
      </div>

      {/* 提及率对比分析图表 */}
      <div className="mention-rate-section">
        {/* 标题 */}
        <div className="section-title-wrapper">
          <span className="section-title">
            {METRICS_CONFIG[
              selectedMetric as keyof typeof METRICS_CONFIG
            ]?.title.replace("分析", t("search_detail.insight_ranking_title"))}
          </span>
        </div>

        <div className="chart-container">
          <MentionRateComparisonChart
            brandComparison={brandComparison}
            brandRatesList={brandRatesList}
            currentMetric={selectedMetric}
            onDownload={onDownload}
          />
        </div>
      </div>

      <div className={`bottom-charts-row ${!isWide ? "mobile-layout" : ""}`}>
        <div className="chart-wrapper">
          {/* 外部标题：AI平台对比分析 */}
          <div className="section-title-wrapper">
            <span className="section-title">
              {t("search_detail.insight_section_title")}
            </span>
          </div>
          <div className="chart-content">
            <PlatformVisibilityChart
              selectedKeys={selectedPlatforms}
              brandAiMetricsTrend={brandAiMetricsTrend as any}
              brandId={brandId}
              brandName={brandName}
              categoryId={categoryId}
              days={days || 7}
              metricType={selectedMetric}
              currentMetric={currentMetric}
            />
          </div>
        </div>
        <div className="chart-wrapper">
          <UserIntentTable
            dataSource={questionListData}
            onDownload={onDownload}
            onQuestionClick={handleQuestionClick}
            containerStyle={{
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          />
        </div>
      </div>

      {/* ContactUs 由 Header 流程统一控制 */}

      {/* QA Browser Modal */}
      <QABrowserModal
        open={qaModalVisible}
        onClose={() => setQaModalVisible(false)}
        question={selectedQuestion}
        answer={selectedAnswer}
        modelName={selectedModelName}
        linksNum={selectedLinksNum}
        taskId={selectedTaskId}
        brandId={brandId}
        categoryId={categoryId}
        days={days}
        metricType={selectedMetric}
        modelId={selectedPlatforms?.length ? selectedPlatforms[0] : "all"}
        onPrevQuestion={handlePrevQuestion}
        onNextQuestion={handleNextQuestion}
        hasPrevQuestion={currentQuestionIndex > 0}
        hasNextQuestion={currentQuestionIndex < visibleQuestionCount - 1}
      />
    </div>
  );
};

export default InsightAnalysisSection;
